import {useEffect, useState} from 'react';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {Box, Grid, Radio, RadioGroup, Stack, Typography} from '@mui/material';
import {DailyCareLogFormFooterButtons} from '../../../../components/daily-care-log/handle-task-form/daily-care-log-form-footer-buttons';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {ScreenPaths} from '../../../../enums';
import {
  getHandleOccurrenceStep,
  HandleOccurrenceStep,
  TransformedOccurrence,
  transformOccurrences,
} from '../../../../utils/data/occurrences';
import moment from 'moment';
import {HandleOccurrenceCard} from '../../office/professional-caregiver/occurrences/handle-card';
import {useAuth} from '../../../../contexts/auth/context';
import {
  EntityOccurrenceAcknowledgement,
  EntityOccurrenceType,
} from '../../../../backend/responses/entity-occurrence';
import {BsCheckAll} from 'react-icons/bs';
import {EntityOccurrencesRepository} from '../../../../repositories/entity-occurrences';
import {useSnackBar} from '../../../../contexts/snack-bar/context';
import {RadioLabel} from '../../../../components/shared/forms/radio';
import {DailyCareInput} from '../../../../components/daily-care-log/forms/daily-care-input';
import {OccurrenceHistory} from '../../../../components/occurrences/occurrences-history/occurrence-history';
import {Tooltip} from '../../../../components/tooltip/tooltip';
import {RolePermissionType} from '../../../../backend/enums/role-permission-type';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {useEntity} from '../../../../contexts/entity/context';
import {ViewAttachments} from '../../../../components/daily-care-log/attachments/view-attachments';
import {
  AddAttachmentsForm,
  SavingAttachment,
} from '../../../../components/daily-care-log/attachments/add-attachments-form';
import {requestAttachmentStorageUrl} from '../unplanned-tasks/unplannedtask.daily-care-log.screen';
import {StorageType} from '../../../../backend/enums';

type HandleOccurrenceDailyCareLogScreenProps = {
  professionals?: AffiliatedUser[];
  seniors?: AffiliatedUser[];
};

export default function HandleOccurrenceDailyCareLogScreen({
  professionals,
  seniors,
}: HandleOccurrenceDailyCareLogScreenProps) {
  const {entityId, occurrenceId}: {entityId: string; occurrenceId: string} = useParams();
  const {t} = useTranslation();
  const dailyCareLogContext = useDailyCareLog();
  const {state: authState, hasWritePermission} = useAuth();
  const location = useLocation();
  let history = useHistory();
  const {
    updateEntityOccurrencesCount,
    state: {occurrencesTypes},
  } = useEntity();
  const snackBarContext = useSnackBar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [occurrence, setOccurrence] = useState<TransformedOccurrence>();
  const [acknowledge, setAcknowledge] = useState<EntityOccurrenceAcknowledgement>();
  const [step, setStep] = useState<HandleOccurrenceStep>();
  const [notes, setNotes] = useState<string>('');
  const [handled, setHandled] = useState<boolean>();
  const [isInputsDisabled, setIsInputsDisabled] = useState<boolean>(false);
  const searchParams = new URLSearchParams(location.search);
  const date = searchParams.get('date');
  const [loading, setLoading] = useState<boolean>(true);
  const [attachments, setAttachments] = useState<SavingAttachment[]>();
  const [errors, setErrors] = useState<boolean>(false);

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(true, t(translations.daily_care_log.generics.back));
    dailyCareLogContext.toggleBottomNavigation(false);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state) {
      const occ = location.state as TransformedOccurrence;
      setOccurrence(occ);
      setLoading(false);
      setAcknowledge(
        occ.acknowledgements.find((ack) => ack.userAuthorId === authState.session?.currentUser.id)
      );
      setStep(getHandleOccurrenceStep(occ, authState.session?.currentUser.id));
      setHandled(occ.markedAsHandled);
      setIsInputsDisabled(
        (occ.markedAsHandled !== undefined && occ.actionRequired) ||
          !hasWritePermission(RolePermissionType.DailyCareLog_Occurrences) ||
          false
      );
    } else {
      const getOccurrence = async (types: EntityOccurrenceType[]) => {
        try {
          const occ = await EntityOccurrencesRepository.instance.getOccurrences(
            entityId,
            moment(date).startOf('day').toISOString(),
            moment(date).endOf('day').toISOString()
          );

          const occurrences = await transformOccurrences(occ, types, seniors, professionals);
          const findOcc = occurrences.find((o) => o.id === occurrenceId);
          if (findOcc) {
            setOccurrence(findOcc);
            setAcknowledge(
              findOcc.acknowledgements.find(
                (ack) => ack.userAuthorId === authState.session?.currentUser.id
              )
            );
            setStep(getHandleOccurrenceStep(findOcc, authState.session?.currentUser.id));
            setHandled(findOcc.markedAsHandled || undefined);
            setIsInputsDisabled(
              (findOcc.markedAsHandled && findOcc.actionRequired) ||
                !hasWritePermission(RolePermissionType.DailyCareLog_Occurrences) ||
                false
            );
          } else {
            console.error(`Occurrence id ${occurrenceId} not found on date ${date}.`);
          }

          setLoading(false);
        } catch (e) {
          console.error('Error on get occurrence to date ' + date, e);
        }
      };
      if (occurrencesTypes) {
        // load occurrences, after open occurrence on notification click
        getOccurrence(occurrencesTypes);
      }
    }
  }, [
    authState.session?.currentUser.id,
    location.state,
    hasWritePermission,
    entityId,
    date,
    professionals,
    occurrencesTypes,
    seniors,
    occurrenceId,
  ]);

  const backToOccurrenceList = () => {
    history.push(
      `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Occurrences}?date=${date}`
    );
  };

  const handleAcknowledge = async () => {
    if (!occurrence) {
      return;
    }
    setIsSubmitting(true);
    try {
      await EntityOccurrencesRepository.instance.acknowledgeOccurrence(
        entityId,
        occurrence.id,
        notes || undefined
      );
      snackBarContext.setDailyCareLogToastNotification({
        severity: 'success',
        message: t(translations.office.professional_caregiver.occurrences.acknowledge_success),
      });
      updateEntityOccurrencesCount();
      if (acknowledge) {
        setAcknowledge({
          ...acknowledge,
          acknowledgedAt: new Date().toISOString(),
        });
      }

      setIsSubmitting(false);
      setStep(getHandleOccurrenceStep(occurrence, authState.session?.currentUser.id, true));
    } catch (error) {
      console.error('Error on acknowledge occurrence.', error);
      snackBarContext.setDailyCareLogToastNotification({
        severity: 'error',
        message: t(translations.office.professional_caregiver.occurrences.acknowledge_error),
      });
      setIsSubmitting(false);
    }
  };

  const handleSaveNotes = async (id: string, notes: string) => {
    setIsSubmitting(true);
    try {
      await EntityOccurrencesRepository.instance.annotateOccurrence(entityId, id, notes);
      await snackBarContext.setDailyCareLogToastNotification({
        severity: 'success',
        message: t(translations.office.professional_caregiver.occurrences.notes_success),
      });
      setIsSubmitting(false);
      backToOccurrenceList();
    } catch (e) {
      console.error('Error on saving notes to occurrence.', e);
      await snackBarContext.setDailyCareLogToastNotification({
        severity: 'error',
        message: t(translations.office.professional_caregiver.occurrences.notes_error),
      });
      setIsSubmitting(false);
      backToOccurrenceList();
    }
  };

  const editOccurrences = async (id: string, attachments: SavingAttachment[]) => {
    const result = await requestAttachmentStorageUrl(
      entityId,
      attachments,
      StorageType.OccurrenceAttachment
    );

    if (!occurrence) {
      Promise.reject("Couldn't find the occurrence");
      return;
    }
    if (result === null) {
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
      });
      Promise.reject("Couldn't upload the attachments");
    } else {
      try {
        await EntityOccurrencesRepository.instance.editOccurrence(
          entityId,
          id,
          occurrence.typeId,
          occurrence.registeredAt,
          occurrence.details,
          result
        );
      } catch (e) {
        console.error('Error on editing occurrence.', e);
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
        });
        Promise.reject("Couldn't edit the occurrence");
      }
    }
  };

  const handleOccurrence = async (id: string, handled: boolean, notes?: string) => {
    if (!entityId || !id) return;
    setIsSubmitting(true);

    if (attachments) {
      await editOccurrences(id, attachments);
    }

    try {
      await EntityOccurrencesRepository.instance.handleOccurrence(entityId, id, handled, notes);
      setIsSubmitting(false);
      await snackBarContext.setDailyCareLogToastNotification({
        severity: 'success',
        message: t(translations.office.professional_caregiver.occurrences.handle_success),
      });
      backToOccurrenceList();
    } catch (e) {
      console.error('Error on handle occurrence.', e);
      setIsSubmitting(false);
      await snackBarContext.setDailyCareLogToastNotification({
        severity: 'error',
        message: t(translations.office.professional_caregiver.occurrences.handle_error),
      });
      backToOccurrenceList();
    }
  };

  if (!occurrence) {
    return null;
  }

  return (
    <Box className="handle-occurrence">
      <Stack spacing={2}>
        <Stack className="title" direction="row" spacing={2}>
          <Typography noWrap>
            {moment(occurrence.registeredAt).format('H[h]mm')}
            <Typography component="span">{occurrence.type}</Typography>
          </Typography>
          {acknowledge?.acknowledgedAt && (
            <Tooltip
              title={t(translations.office.professional_caregiver.occurrences.took_acknowledge)}
            >
              <Stack
                gap="2px"
                flexDirection="row"
                className="acknowledge-badge"
                alignItems="center"
              >
                <BsCheckAll />
              </Stack>
            </Tooltip>
          )}
        </Stack>
        <HandleOccurrenceCard isLoading={loading} isDailyCareApp data={occurrence} />
        {occurrence.actionRequired && (step === 'handle' || step === 'handled-edit') && (
          <RadioGroup row>
            <Grid container columnSpacing={2} rowSpacing={4}>
              <Grid item xs={6}>
                <RadioLabel
                  value="done"
                  disabled={isInputsDisabled}
                  control={<Radio />}
                  label={t(translations.office.professional_caregiver.occurrences.status.handled)}
                  checked={handled === true}
                  onChange={(event, checked) => {
                    if (checked) {
                      setHandled(true);
                    } else {
                      setHandled(false);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <RadioLabel
                  value="not-done"
                  disabled={isInputsDisabled}
                  control={<Radio />}
                  label={t(
                    translations.office.professional_caregiver.occurrences.status.not_handled
                  )}
                  checked={handled === false}
                  onChange={(event, checked) => {
                    if (checked) {
                      setHandled(false);
                    } else {
                      setHandled(true);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </RadioGroup>
        )}
        {(step === 'handle' || step === 'handled-edit' || step === 'insert-notes-only') && (
          <DailyCareInput
            disabled={isInputsDisabled}
            placeholder={t(translations.office.professional_caregiver.occurrences.write_notes)}
            value={notes}
            multiline
            rows="3"
            onChange={(event) => setNotes(event.target.value)}
          />
        )}
        {occurrence.attachments && occurrence.attachments.length > 0 && (
          <ViewAttachments isDailyCareLogApp attachments={occurrence.attachments} />
        )}
        {!isInputsDisabled &&
          (step === 'handle' || step === 'handled-edit') &&
          occurrence.registeredBy?.id === authState.session?.currentUser.id && (
            <AddAttachmentsForm
              type="view"
              attachmentsUrls={occurrence.attachments}
              onAttachmentsChange={(value) => setAttachments(value)}
              onError={(errors) => {
                if (errors.length > 0) {
                  setErrors(true);
                } else {
                  setErrors(false);
                }
              }}
            />
          )}
        {occurrence.history && occurrence.history.length > 0 && (
          <OccurrenceHistory
            isDailyCareLogApp
            history={occurrence.history}
            professionals={professionals}
          />
        )}
      </Stack>
      <DailyCareLogFormFooterButtons
        canWrite
        editMode={step === 'handled-edit' ? false : true}
        isCancelDisabled={isSubmitting}
        isSaveDisabled={
          isSubmitting ||
          loading ||
          step === 'unknown' ||
          (step === 'insert-notes-only' && !notes) ||
          (step === 'handle' && handled === undefined && occurrence.status !== 'registered') ||
          (step === 'handle' && !notes && !attachments && occurrence.status === 'registered') ||
          !hasWritePermission(RolePermissionType.DailyCareLog_Occurrences) ||
          errors
        }
        isSaveLoading={isSubmitting}
        isCancelVisible={step === 'acknowledge' ? false : true}
        saveLabel={
          step === 'acknowledge'
            ? t(translations.office.professional_caregiver.occurrences.acknowledge)
            : t(translations.daily_care_log.task_details.save)
        }
        onCancel={backToOccurrenceList}
        onSave={() => {
          if (step === 'acknowledge') {
            handleAcknowledge();
          } else if (step === 'handle' && handled !== undefined) {
            setIsSubmitting(true);
            if (notes) {
              handleOccurrence(occurrence.id, handled, notes);
            } else {
              handleOccurrence(occurrence.id, handled);
            }
          } else if (step === 'insert-notes-only') {
            if (notes) {
              handleSaveNotes(occurrence.id, notes);
            }
          }
        }}
        onEdit={() => {
          if (step === 'handled-edit' && occurrence.markedAsHandled !== undefined) {
            setIsInputsDisabled(false);
            setStep('handle');
          }
        }}
      />
    </Box>
  );
}
